import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bell, Plus, Trash2, Loader2, Repeat, RefreshCw, AlertTriangle } from 'lucide-react'
import { ConfirmModal } from '../common/ConfirmModal'
import { useReminders, useCreateReminder, useDeleteReminder, useRetryReminder } from '../../hooks/useReminders'
import type { CreateReminderRequest } from '../../services/remindersApi'

interface ReminderFormProps {
  koblinkId: string
  actorId?: string
}

const REMINDER_TYPES = ['expiry', 'renewal', 'review', 'custom'] as const
const RECURRENCE_TYPES = ['none', 'daily', 'weekly', 'monthly', 'yearly'] as const

const statusColors: Record<string, string> = {
  pending: 'text-warning-500',
  sent: 'text-success-500',
  failed: 'text-error-500',
}

export function ReminderSection({ koblinkId, actorId }: ReminderFormProps) {
  const { t } = useTranslation()
  const { data: reminders, isLoading } = useReminders(koblinkId, actorId)
  const createMutation = useCreateReminder()
  const deleteMutation = useDeleteReminder()
  const retryMutation = useRetryReminder()
  const [showForm, setShowForm] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null)
  const [useDaysBefore, setUseDaysBefore] = useState(false)
  const [formData, setFormData] = useState<CreateReminderRequest>({
    reminder_type: 'expiry',
    remind_at: '',
    message: '',
    recurrence: 'none',
  })

  const handleCreate = () => {
    // Either remind_at or days_before_expiry must be set
    if (!formData.remind_at && !formData.days_before_expiry) return

    const data = { ...formData }
    // If using days_before_expiry, remove remind_at (backend calculates it)
    if (useDaysBefore && data.days_before_expiry) {
      delete data.remind_at
    }
    // Don't send recurrence_end_date if recurrence is none
    if (data.recurrence === 'none') {
      delete data.recurrence_end_date
    }

    createMutation.mutate(
      { koblinkId, data, actorId },
      {
        onSuccess: () => {
          setShowForm(false)
          setUseDaysBefore(false)
          setFormData({ reminder_type: 'expiry', remind_at: '', message: '', recurrence: 'none' })
        },
      },
    )
  }

  return (
    <div>
      {/* Existing reminders */}
      {isLoading && (
        <p className="text-[11px] text-neutral-400 py-1">{t('common.loading')}</p>
      )}
      {reminders && reminders.length > 0 && (
        <div className="space-y-1 mb-2">
          {reminders.map((reminder) => (
            <div
              key={reminder.id}
              className="flex items-center gap-2 py-1 px-2 rounded bg-neutral-50 dark:bg-neutral-800/50"
            >
              <Bell size={10} className={statusColors[reminder.status] || 'text-neutral-400'} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-neutral-700 dark:text-neutral-300">
                    {t(`followup.reminderType.${reminder.reminder_type}`)}
                  </span>
                  {reminder.recurrence && reminder.recurrence !== 'none' && (
                    <span title={t(`followup.recurrenceType.${reminder.recurrence}`)}>
                      <Repeat size={8} className="text-neutral-400" />
                    </span>
                  )}
                </div>
                <span className="text-[10px] text-neutral-400">
                  {reminder.days_before_expiry
                    ? t('followup.daysBeforeExpiry') + `: ${reminder.days_before_expiry}`
                    : new Date(reminder.remind_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                {reminder.status === 'failed' && (
                  <span className="text-[9px] text-error-500 ml-1">
                    <AlertTriangle size={8} className="inline" /> {t('followup.reminderFailed')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {reminder.status === 'failed' && reminder.can_retry && (
                  <button
                    onClick={() => retryMutation.mutate(reminder.id)}
                    disabled={retryMutation.isPending}
                    className="p-0.5 text-warning-500 hover:text-warning-600 transition-colors"
                    title={t('followup.retryReminder')}
                  >
                    {retryMutation.isPending ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                  </button>
                )}
                <button
                  onClick={() => setDeleteTarget(reminder.id)}
                  disabled={deleteMutation.isPending}
                  className="p-0.5 text-neutral-400 hover:text-error-500 transition-colors"
                >
                  <Trash2 size={10} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add reminder */}
      {!showForm ? (
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-1 text-[11px] text-primary-500 hover:text-primary-600 transition-colors"
        >
          <Plus size={10} />
          {t('followup.addReminder')}
        </button>
      ) : (
        <div className="space-y-2 p-2 rounded border border-neutral-200 dark:border-neutral-700">
          {/* Reminder type */}
          <select
            value={formData.reminder_type}
            onChange={(e) => {
              const type = e.target.value as typeof REMINDER_TYPES[number]
              setFormData((prev) => ({ ...prev, reminder_type: type }))
              // Reset days-before mode when switching away from expiry
              if (type !== 'expiry') setUseDaysBefore(false)
            }}
            className="w-full text-[11px] px-2 py-1 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
          >
            {REMINDER_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`followup.reminderType.${type}`)}
              </option>
            ))}
          </select>

          {/* Days before expiry toggle (only for expiry type) */}
          {formData.reminder_type === 'expiry' && (
            <label className="flex items-center gap-1.5 text-[11px] text-neutral-600 dark:text-neutral-400 cursor-pointer">
              <input
                type="checkbox"
                checked={useDaysBefore}
                onChange={(e) => {
                  setUseDaysBefore(e.target.checked)
                  if (e.target.checked) {
                    setFormData((prev) => ({ ...prev, days_before_expiry: 30, remind_at: '' }))
                  } else {
                    setFormData((prev) => ({ ...prev, days_before_expiry: undefined }))
                  }
                }}
                className="rounded border-neutral-300"
              />
              {t('followup.daysBeforeExpiry')}
            </label>
          )}

          {/* Days before expiry input */}
          {useDaysBefore ? (
            <input
              type="number"
              min={1}
              max={365}
              value={formData.days_before_expiry || 30}
              onChange={(e) => setFormData((prev) => ({ ...prev, days_before_expiry: parseInt(e.target.value) || undefined }))}
              className="w-full text-[11px] px-2 py-1 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
            />
          ) : (
            /* Date picker (hidden when using days-before) */
            <input
              type="date"
              value={formData.remind_at ? formData.remind_at.substring(0, 10) : ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, remind_at: e.target.value ? `${e.target.value}T09:00:00Z` : '' }))}
              className="w-full text-[11px] px-2 py-1 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
            />
          )}

          {/* Recurrence */}
          <select
            value={formData.recurrence || 'none'}
            onChange={(e) => setFormData((prev) => ({ ...prev, recurrence: e.target.value as typeof RECURRENCE_TYPES[number] }))}
            className="w-full text-[11px] px-2 py-1 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
          >
            {RECURRENCE_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`followup.recurrenceType.${type}`)}
              </option>
            ))}
          </select>

          {/* Recurrence end date (shown when recurrence is not none) */}
          {formData.recurrence && formData.recurrence !== 'none' && (
            <div>
              <label className="text-[10px] text-neutral-500 dark:text-neutral-400">
                {t('followup.recurrenceEndDate')}
              </label>
              <input
                type="date"
                value={formData.recurrence_end_date || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, recurrence_end_date: e.target.value || undefined }))}
                className="w-full text-[11px] px-2 py-1 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
              />
            </div>
          )}

          {/* Message */}
          <input
            type="text"
            value={formData.message || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
            placeholder={t('followup.reminderMessagePlaceholder')}
            className="w-full text-[11px] px-2 py-1 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 placeholder-neutral-400"
          />

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              disabled={(!formData.remind_at && !formData.days_before_expiry) || createMutation.isPending}
              className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] py-1 rounded bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 transition-colors"
            >
              {createMutation.isPending ? <Loader2 size={10} className="animate-spin" /> : <Bell size={10} />}
              {t('common.save')}
            </button>
            <button
              onClick={() => {
                setShowForm(false)
                setUseDaysBefore(false)
              }}
              className="flex-1 text-[11px] py-1 rounded border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        open={deleteTarget != null}
        title={t('followup.deleteReminderTitle', 'Delete reminder?')}
        description={t('followup.deleteReminderDescription', 'This reminder will be permanently removed.')}
        variant="danger"
        isPending={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTarget != null) {
            deleteMutation.mutate(deleteTarget, { onSuccess: () => setDeleteTarget(null) })
          }
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
